import React from 'react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Description, ErrorMessage, Field, FieldGroup, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import type { VoiceAgentTransferLineOfBusinessInput } from './voice_agent_form_input';

type NewLineOfBusiness = Pick<
  VoiceAgentTransferLineOfBusinessInput,
  'label' | 'description' | 'examples'
>;

function removalDescription(label: string, assignedEntryCount: number): string {
  if (assignedEntryCount === 0) {
    return `${label} will be removed from the catalog.`;
  }
  return `${label} will also be removed from ${assignedEntryCount} directory ${
    assignedEntryCount === 1 ? 'entry' : 'entries'
  }.`;
}

function LineOfBusinessFields({
  label,
  description,
  examples,
  onLabelChange,
  onDescriptionChange,
  onExamplesChange
}: {
  label: string;
  description: string;
  examples: string;
  onLabelChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onExamplesChange: (value: string) => void;
}) {
  return (
    <FieldGroup>
      <Field>
        <Label>Name</Label>
        <Input
          value={label}
          onChange={(event) => onLabelChange(event.currentTarget.value)}
          placeholder="e.g., Life & Health"
        />
      </Field>
      <Field>
        <Label>Description</Label>
        <Input
          value={description}
          onChange={(event) => onDescriptionChange(event.currentTarget.value)}
          placeholder="e.g., Individual life and health insurance"
        />
      </Field>
      <Field>
        <Label>Example caller phrases</Label>
        <Input
          value={examples}
          onChange={(event) => onExamplesChange(event.currentTarget.value)}
          placeholder="e.g., life policy, individual health plan"
        />
        <Description>Separate phrases with commas.</Description>
      </Field>
    </FieldGroup>
  );
}

export function VoiceLineOfBusinessCatalogEditor({
  catalog,
  builtInIds,
  assignedEntryCountById,
  fieldId,
  validationError,
  onAdd,
  onUpdate,
  onRemove
}: {
  catalog: VoiceAgentTransferLineOfBusinessInput[];
  builtInIds: ReadonlySet<string>;
  assignedEntryCountById: ReadonlyMap<string, number>;
  fieldId: string;
  validationError?: string;
  onAdd: (lineOfBusiness: NewLineOfBusiness) => void;
  onUpdate: (lineOfBusiness: VoiceAgentTransferLineOfBusinessInput) => void;
  onRemove: (id: string) => void;
}) {
  const [customLabel, setCustomLabel] = React.useState('');
  const [customDescription, setCustomDescription] = React.useState('');
  const [customExamples, setCustomExamples] = React.useState('');
  const [editingLineOfBusiness, setEditingLineOfBusiness] =
    React.useState<VoiceAgentTransferLineOfBusinessInput | null>(null);
  const [lineOfBusinessToRemove, setLineOfBusinessToRemove] =
    React.useState<VoiceAgentTransferLineOfBusinessInput | null>(null);
  const parsedExamples = customExamples
    .split(',')
    .map((example) => example.trim())
    .filter(Boolean);

  return (
    <Field data-field-id={fieldId}>
      <Label>Transfer Lines of Business</Label>
      <Description>
        Shared outer routing segments used by both internal and external transfer directories.
        Built-in lines cannot be removed.
      </Description>
      {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
      <div data-slot="control" className="mt-3 max-w-xl space-y-2">
        {catalog.map((entry) => {
          const isBuiltIn = builtInIds.has(entry.id);
          return (
            <div
              key={entry.id}
              className="relative flex h-10 items-center justify-between gap-3 rounded-lg border border-neutral-200 px-3 dark:border-neutral-700"
            >
              <span className="truncate text-sm text-neutral-900 dark:text-white">
                {entry.label}
              </span>
              {isBuiltIn ? (
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Built-in</span>
              ) : (
                <div className="absolute right-1 top-1/2 flex -translate-y-1/2">
                  <Button
                    type="button"
                    plain
                    className="p-1.5"
                    aria-label={`Edit ${entry.label}`}
                    onClick={() => setEditingLineOfBusiness({ ...entry })}
                  >
                    <PencilSquareIcon data-slot="icon" />
                  </Button>
                  <Button
                    type="button"
                    plain
                    className="p-1.5 text-red-600 dark:text-red-400"
                    aria-label={`Remove ${entry.label}`}
                    onClick={() => setLineOfBusinessToRemove(entry)}
                  >
                    <TrashIcon data-slot="icon" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        <div className="grid gap-3 pt-2">
          <LineOfBusinessFields
            label={customLabel}
            description={customDescription}
            examples={customExamples}
            onLabelChange={setCustomLabel}
            onDescriptionChange={setCustomDescription}
            onExamplesChange={setCustomExamples}
          />
          <Button
            type="button"
            color="sky"
            className="w-fit whitespace-nowrap"
            disabled={
              !customLabel.trim() || !customDescription.trim() || parsedExamples.length === 0
            }
            onClick={() => {
              onAdd({
                label: customLabel.trim(),
                description: customDescription.trim(),
                examples: parsedExamples
              });
              setCustomLabel('');
              setCustomDescription('');
              setCustomExamples('');
            }}
          >
            Add
          </Button>
        </div>
      </div>

      <Dialog
        open={editingLineOfBusiness !== null}
        onClose={() => setEditingLineOfBusiness(null)}
        size="md"
      >
        <DialogTitle>Edit line of business</DialogTitle>
        <DialogDescription>
          Give Cara enough context to classify how callers describe this line of business.
        </DialogDescription>
        {editingLineOfBusiness && (
          <>
            <DialogBody>
              <LineOfBusinessFields
                label={editingLineOfBusiness.label}
                description={editingLineOfBusiness.description}
                examples={editingLineOfBusiness.examples.join(', ')}
                onLabelChange={(label) =>
                  setEditingLineOfBusiness({ ...editingLineOfBusiness, label })
                }
                onDescriptionChange={(description) =>
                  setEditingLineOfBusiness({ ...editingLineOfBusiness, description })
                }
                onExamplesChange={(examples) =>
                  setEditingLineOfBusiness({
                    ...editingLineOfBusiness,
                    examples: examples.split(',')
                  })
                }
              />
            </DialogBody>
            <DialogActions>
              <Button type="button" plain onClick={() => setEditingLineOfBusiness(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                color="sky"
                disabled={
                  !editingLineOfBusiness.label.trim() ||
                  !editingLineOfBusiness.description.trim() ||
                  editingLineOfBusiness.examples.every((example) => !example.trim())
                }
                onClick={() => {
                  onUpdate({
                    ...editingLineOfBusiness,
                    label: editingLineOfBusiness.label.trim(),
                    description: editingLineOfBusiness.description.trim(),
                    examples: editingLineOfBusiness.examples
                      .map((example) => example.trim())
                      .filter(Boolean)
                  });
                  setEditingLineOfBusiness(null);
                }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={lineOfBusinessToRemove !== null}
        onClose={() => setLineOfBusinessToRemove(null)}
        size="md"
      >
        <DialogTitle>Remove line of business?</DialogTitle>
        {lineOfBusinessToRemove && (
          <>
            <DialogDescription>
              {removalDescription(
                lineOfBusinessToRemove.label,
                assignedEntryCountById.get(lineOfBusinessToRemove.id) ?? 0
              )}
            </DialogDescription>
            <DialogActions>
              <Button type="button" plain onClick={() => setLineOfBusinessToRemove(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                color="red"
                onClick={() => {
                  onRemove(lineOfBusinessToRemove.id);
                  setLineOfBusinessToRemove(null);
                }}
              >
                Remove
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Field>
  );
}
